import type { HulpvraagRij } from '../gesprekken/gesprekken.api';

export type VraagVelden = { onderwerp: string; deelonderwerp: string; vraag: string };

/** Een regel die de buddy er zelf bij heeft gezet. Hij bestaat nog niet op de
 *  server, dus hij heeft een sleutel van dit scherm en geen id. */
export interface NieuweRegel {
  sleutel: number;
  velden: VraagVelden;
}

/** Eén hulpvraag in een groep: gevonden door het model of er zelf bij gezet. */
export type Lid =
  | { soort: 'gevonden'; hulpvraag: HulpvraagRij; velden: VraagVelden }
  | { soort: 'nieuw'; sleutel: number; velden: VraagVelden };

export interface Groep {
  /** Waarop is gegroepeerd. Voor een groep met een naam de genormaliseerde
   *  naam; voor een naamloze nieuwe regel iets dat alleen bij die regel hoort. */
  sleutel: string;
  /** Zoals hij op het scherm staat: de spelling van het eerste lid. */
  naam: string;
  /** Dit onderwerp bestaat nog niet in de taxonomie. */
  nieuwOnderwerp: boolean;
  leden: Lid[];
}

/**
 * Twee namen zijn hetzelfde onderwerp als ze na deze bewerking gelijk zijn.
 *
 * Kleinletters en zonder buitenste witruimte, net als de `normaal`-kolom op de
 * server. Wie "p&id" typt terwijl "P&ID" bestaat, bedoelt hetzelfde onderwerp, en
 * bij het vaststellen belanden ze ook op dezelfde rij. Dan horen ze hier ook al
 * bij elkaar te staan.
 */
export const normaliseer = (naam: string) => naam.trim().toLowerCase();

/** De bestaande spelling van een onderwerp, als het al bestaat. Zo wordt "p&id"
 *  bij het verlaten van het veld "P&ID" en zie je dat het is samengevoegd. */
export function bestaandeSpelling(naam: string, bekend: readonly string[]): string | null {
  const sleutel = normaliseer(naam);
  if (!sleutel) return null;
  return bekend.find((n) => normaliseer(n) === sleutel) ?? null;
}

/**
 * De groepen zoals ze op het scherm staan: per hoofdonderwerp, in de volgorde
 * waarin ze binnenkwamen.
 *
 * Gegroepeerd op de naam uit het klad en niet op die van de server, want anders
 * kan een hulpvraag die je onder een ander onderwerp hangt daar nooit
 * terechtkomen. Dat het klad pas bij het verlaten van het veld wordt bijgewerkt
 * is wat dit werkbaar maakt: zou het per toetsaanslag gaan, dan sprong een regel
 * naar een andere groep terwijl je de naam nog aan het typen was.
 *
 * Zelf toegevoegde regels zitten in dezelfde groepen. Een regel die je onder een
 * bestaand onderwerp aanmaakt heeft die naam al, en staat dus meteen op de juiste
 * kaart. Een regel zonder naam krijgt een sleutel die alleen bij hem hoort, zodat
 * hij zijn eigen kaart blijft in plaats van samen te klonteren met elke andere
 * naamloze regel.
 */
export function bouwGroepen(
  gevonden: readonly HulpvraagRij[],
  nieuwe: readonly NieuweRegel[],
  klad: Record<string, VraagVelden>,
  bekendeOnderwerpen: readonly string[],
): Groep[] {
  const per = new Map<string, Groep>();

  const voegToe = (sleutel: string, naam: string, lid: Lid) => {
    const groep = per.get(sleutel);
    if (groep) groep.leden.push(lid);
    else per.set(sleutel, { sleutel, naam, nieuwOnderwerp: false, leden: [lid] });
  };

  for (const h of gevonden) {
    const velden = klad[h.id] ?? {
      onderwerp: h.onderwerpNaam,
      deelonderwerp: h.deelonderwerpNaam,
      vraag: h.vraag,
    };
    voegToe(normaliseer(velden.onderwerp), velden.onderwerp, {
      soort: 'gevonden',
      hulpvraag: h,
      velden,
    });
  }

  for (const n of nieuwe) {
    const sleutel = normaliseer(n.velden.onderwerp) || `naamloos-${n.sleutel}`;
    voegToe(sleutel, n.velden.onderwerp, {
      soort: 'nieuw',
      sleutel: n.sleutel,
      velden: n.velden,
    });
  }

  for (const groep of per.values()) {
    const gevondenLeden = groep.leden.filter((l) => l.soort === 'gevonden');
    groep.nieuwOnderwerp =
      groep.naam.trim() !== '' &&
      !bekendeOnderwerpen.some((n) => normaliseer(n) === groep.sleutel) &&
      // Zoals de server het zag toen hij ze vond. Bij een groep die de buddy
      // zelf heeft getypt zijn er geen gevonden leden en beslist de taxonomie.
      gevondenLeden.every((l) => l.soort === 'gevonden' && l.hulpvraag.nieuwOnderwerp);
  }

  return [...per.values()];
}
